import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { AlertCircle, FileText, Loader2, Mail } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { subscriberMeApi } from '../../../api/subscriberMeApi';
import { SubscriberSelfDetailPanel } from './SubscriberSelfDetailPanel';
import { useUiLocale } from '../../../contexts/UiLocaleContext';
import {
  DeviceSwitcher,
  PreviewHeader,
  buildEmailPreviewPolishCss,
  type PreviewDevice,
} from './newsletterPreviewParts';
import './SubscriberNewslettersPage.css';

type TabValue = 'newsletters' | 'topics';

const PREVIEW_DEVICE_CONFIG: {
  id: PreviewDevice;
  labelKey: string;
  width: number;
  height: number;
}[] = [
  { id: 'iphone', labelKey: 'dataroom.deviceIphone', width: 390, height: 844 },
  { id: 'android', labelKey: 'dataroom.deviceAndroid', width: 412, height: 915 },
  { id: 'pc', labelKey: 'dataroom.devicePc', width: 1024, height: 720 },
];

export type NewsletterListItem = {
  id: string;
  title: string;
  sentAt: string;
  previewLine: string;
  from: string;
  subject: string;
};

function formatDate(iso: string) {
  try {
    return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function parseTab(raw: string | null): TabValue {
  return raw === 'topics' ? 'topics' : 'newsletters';
}

export function SubscriberNewslettersPage() {
  const reduceMotion = useReducedMotion();
  const { t } = useUiLocale();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = parseTab(searchParams.get('tab'));
  const [previewDevice, setPreviewDevice] = useState<PreviewDevice>('pc');

  const setActiveTab = (tab: TabValue) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (tab === 'topics') next.set('tab', 'topics');
        else next.delete('tab');
        return next;
      },
      { replace: true },
    );
  };

  const [newsletters, setNewsletters] = useState<NewsletterListItem[]>([]);
  const [loadStatus, setLoadStatus] = useState<'idle' | 'loading' | 'ok' | 'error'>('idle');
  const [detailHtml, setDetailHtml] = useState<string | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedNewsletterId, setSelectedNewsletterId] = useState<string | null>(null);

  useEffect(() => {
    let c = false;
    (async () => {
      setLoadStatus('loading');
      try {
        const nlRes = await subscriberMeApi.getNewsletters({ skip: 0, limit: 50 });
        if (c) return;
        const page = nlRes.data;
        const items = Array.isArray(page?.items) ? page.items : [];
        const mapped: NewsletterListItem[] = items.map((row: Record<string, unknown>) => ({
          id: String(row.id),
          title: String(row.subject ?? 'Newsletter'),
          sentAt: String(row.sent_at ?? ''),
          previewLine: String(row.preview_line ?? ''),
          from: String(row.from_email ?? ''),
          subject: String(row.subject ?? ''),
        }));
        setNewsletters(mapped);
        if (mapped.length > 0) {
          setSelectedNewsletterId((prev) => prev ?? mapped[0].id);
        } else {
          setSelectedNewsletterId(null);
        }
        setLoadStatus('ok');
      } catch {
        if (!c) {
          setNewsletters([]);
          setSelectedNewsletterId(null);
          setLoadStatus('error');
        }
      }
    })();
    return () => {
      c = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedNewsletterId) {
      setDetailHtml(null);
      setDetailLoading(false);
      return;
    }
    let c = false;
    setDetailLoading(true);
    setDetailHtml(null);
    subscriberMeApi
      .getNewsletter(selectedNewsletterId)
      .then((res) => {
        if (!c) setDetailHtml(typeof res.data?.body_html === 'string' ? res.data.body_html : null);
      })
      .catch(() => {
        if (!c) setDetailHtml(null);
      })
      .finally(() => {
        if (!c) setDetailLoading(false);
      });
    return () => {
      c = true;
    };
  }, [selectedNewsletterId]);

  const selectedNewsletter = useMemo(
    () => newsletters.find((n) => n.id === selectedNewsletterId) ?? null,
    [selectedNewsletterId, newsletters],
  );

  const selectedPreviewDevice = useMemo(
    () => PREVIEW_DEVICE_CONFIG.find((d) => d.id === previewDevice) ?? PREVIEW_DEVICE_CONFIG[2],
    [previewDevice],
  );

  const deviceOptions = useMemo(
    () =>
      PREVIEW_DEVICE_CONFIG.map((device) => ({
        id: device.id,
        label: t(device.labelKey),
      })),
    [t],
  );

  const centerKey = selectedNewsletterId ? `nl-${selectedNewsletterId}` : 'nl-empty';

  const centerTransition = reduceMotion
    ? { duration: 0 }
    : { duration: 0.18, ease: [0.25, 0.46, 0.45, 0.94] as const };

  const isPc = previewDevice === 'pc';

  return (
    <div className="sn-page">
      <section className="sn-preview" aria-label="Preview">
        <PreviewHeader
          device={previewDevice}
          switcher={
            <DeviceSwitcher
              devices={deviceOptions}
              value={previewDevice}
              onChange={setPreviewDevice}
              ariaLabel={t('dataroom.previewDeviceSizeAria')}
            />
          }
        />

        <div
          className={[
            'sn-preview-body sn-scroll',
            isPc ? '' : 'is-device',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={`${centerKey}-${previewDevice}`}
              className={[
                'flex min-h-0 min-w-0 flex-1 flex-col',
                isPc ? 'h-full w-full' : 'w-full items-center justify-center',
              ].join(' ')}
              initial={reduceMotion ? false : { opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={reduceMotion ? undefined : { opacity: 0, y: -6 }}
              transition={centerTransition}
            >
              {selectedNewsletter ? (
                detailLoading ? (
                  <div className="sn-loading">
                    <Loader2 className="size-8 animate-spin text-[#2563EB]" aria-hidden />
                    <span>Loading email…</span>
                  </div>
                ) : detailHtml ? (
                  <div
                    className={[
                      'sn-content-surface',
                      isPc ? 'is-pc' : 'is-phone',
                    ].join(' ')}
                    style={
                      isPc
                        ? undefined
                        : {
                            width: selectedPreviewDevice.width,
                            maxWidth: '100%',
                            height: Math.min(
                              selectedPreviewDevice.height,
                              typeof window !== 'undefined'
                                ? window.innerHeight - 220
                                : selectedPreviewDevice.height,
                            ),
                            maxHeight: 'calc(100dvh - 14rem)',
                          }
                    }
                  >
                    <EmailHtmlFrame
                      html={detailHtml}
                      title={`${selectedNewsletter.subject} (${t(selectedPreviewDevice.labelKey)})`}
                      fill
                    />
                  </div>
                ) : (
                  <EmptyCenter
                    icon={<Mail className="h-10 w-10 text-[#CBD5E1]" />}
                    title="No HTML content"
                    description="This issue has no HTML body to preview."
                    reduceMotion={reduceMotion}
                  />
                )
              ) : loadStatus === 'loading' ? (
                <div className="sn-loading">
                  <Loader2 className="size-8 animate-spin text-[#2563EB]" aria-hidden />
                  <span>Loading…</span>
                </div>
              ) : (
                <EmptyCenter
                  icon={<Mail className="h-10 w-10 text-[#CBD5E1]" />}
                  title="No newsletters yet"
                  description="When your organization sends you issues, they will appear in the list on the right."
                  reduceMotion={reduceMotion}
                />
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      <aside className="sn-sidebar" aria-label="Newsletter controls">
        <div className="sn-sidebar-header">
          <h1>My Newsletters</h1>
          <p>Past issues and subscribed topics</p>
        </div>

        <div className="sn-tabs" role="tablist" aria-label="Sidebar views">
          <a
            href="/newsletters"
            role="tab"
            aria-selected={activeTab === 'newsletters'}
            onClick={(e) => {
              e.preventDefault();
              setActiveTab('newsletters');
            }}
            className={['sn-tab', activeTab === 'newsletters' ? 'is-active' : '']
              .filter(Boolean)
              .join(' ')}
          >
            Past emails
          </a>
          <a
            href="/newsletters?tab=topics"
            role="tab"
            aria-selected={activeTab === 'topics'}
            data-testid="topics-tab"
            onClick={(e) => {
              e.preventDefault();
              setActiveTab('topics');
            }}
            className={['sn-tab', activeTab === 'topics' ? 'is-active' : '']
              .filter(Boolean)
              .join(' ')}
          >
            Topics
          </a>
        </div>

        {activeTab === 'newsletters' ? (
          <div className="flex min-h-0 flex-1 flex-col">
            <p className="sn-list-label">Previously sent</p>
            <div
              className="sn-list sn-scroll"
              role="list"
              aria-label="Past newsletters"
            >
              {loadStatus === 'error' ? (
                <div className="sn-error-card">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-red-50">
                    <AlertCircle className="h-5 w-5 text-red-600" />
                  </span>
                  <p>Could not load newsletters.</p>
                </div>
              ) : newsletters.length === 0 ? (
                <p className="px-3 py-8 text-center text-sm text-[#64748B]">No past emails yet.</p>
              ) : (
                newsletters.map((n, i) => {
                  const selected = selectedNewsletterId === n.id;
                  return (
                    <motion.button
                      key={n.id}
                      type="button"
                      role="listitem"
                      onClick={() => setSelectedNewsletterId(n.id)}
                      initial={reduceMotion ? false : { opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{
                        delay: reduceMotion ? 0 : 0.04 + i * 0.03,
                        duration: reduceMotion ? 0 : 0.2,
                        ease: 'easeOut',
                      }}
                      whileTap={reduceMotion ? undefined : { scale: 0.99 }}
                      className={['sn-mail-item', selected ? 'is-selected' : '']
                        .filter(Boolean)
                        .join(' ')}
                    >
                      <span className="sn-mail-item-title">
                        <FileText className="mt-0.5 h-4 w-4 shrink-0 text-[#2563EB]" aria-hidden />
                        <span>{n.title}</span>
                      </span>
                      <span className="sn-mail-item-date">{formatDate(n.sentAt)}</span>
                      <span className="sn-mail-item-preview">{n.previewLine}</span>
                    </motion.button>
                  );
                })
              )}
            </div>
          </div>
        ) : (
          <div className="sn-topics-panel">
            <SubscriberSelfDetailPanel variant="sidebar" />
          </div>
        )}
      </aside>
    </div>
  );
}

function EmailHtmlFrame({
  html,
  title,
  fill = false,
}: {
  html: string;
  title: string;
  fill?: boolean;
}) {
  const srcDoc = useMemo(() => {
    const raw = html.trim();
    if (!raw) return '';
    const polish = buildEmailPreviewPolishCss();
    if (/^<!DOCTYPE|^<html/i.test(raw)) {
      if (/<\/head>/i.test(raw)) {
        return raw.replace(/<\/head>/i, `${polish}</head>`);
      }
      return raw.replace(/<html[^>]*>/i, (m) => `${m}<head>${polish}</head>`);
    }
    return `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">${polish}</head><body>${raw}</body></html>`;
  }, [html]);

  if (!srcDoc) return null;

  return (
    <iframe
      title={title}
      sandbox="allow-same-origin"
      className={
        fill
          ? 'block h-full w-full min-h-0 flex-1 border-0 bg-white'
          : 'block h-[min(70vh,720px)] min-h-[320px] max-h-[calc(100vh-10rem)] w-full min-w-0 rounded-[16px] border border-[#DCE5F2] bg-white'
      }
      srcDoc={srcDoc}
    />
  );
}

function EmptyCenter({
  icon,
  title,
  description,
  reduceMotion,
}: {
  icon: ReactNode;
  title: string;
  description: string;
  reduceMotion: boolean | null;
}) {
  return (
    <motion.div
      className="sn-empty"
      initial={reduceMotion ? false : { opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={
        reduceMotion ? { duration: 0 } : { type: 'spring', stiffness: 340, damping: 28 }
      }
    >
      <motion.div
        initial={reduceMotion ? false : { scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: reduceMotion ? 0 : 0.05, duration: reduceMotion ? 0 : 0.2 }}
      >
        {icon}
      </motion.div>
      <h2>{title}</h2>
      <p>{description}</p>
    </motion.div>
  );
}
